import { PropsWithChildren, useEffect, useRef, useState } from 'react'
import { Drawer } from 'components/Drawer'
import { DrawerProps } from 'components/Drawer/Root'
import { Heading } from 'blaze-ui/Heading/Heading'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { WithPadding } from 'components/Drawer/Types'
import { setCameraToCurrentFrame } from 'components/VisualStudioPlayground/Shapes/FrameShape/helpers'
import { getCurrentFrame } from 'components/VisualStudioPlayground/VisualEditor/helpers'
import { getCaptionPadding } from 'components/Design/utils/getInitialSceneBounds'
import { MOBILE_DESIGN_FOOTER_ID } from 'components/Design/Footer/constants'

interface DesignerDrawerProps extends DrawerProps {
  title: string
  closeButton?: JSX.Element
  onClose?: () => void
  onBack?: () => void
  enableSnap?: boolean
  disableDragToClose?: boolean
  initialSnap?: number
}

export const DesignerDrawer = observer((props: PropsWithChildren<DesignerDrawerProps & WithPadding>) => {
  const {
    children,
    title,
    closeButton,
    onClose,
    onBack,
    enableSnap = false,
    disableDragToClose,
    initialSnap = 0.5,
    open,
    padding = '5px 15px 15px 15px',
    ...rest
  } = props
  const { designStore, drawersStore } = useRootStore()
  const initialRender = useRef(true)
  const [snap, setSnap] = useState<string | number | null>(initialSnap)
  const snapPoints = enableSnap ? [initialSnap, 1] : [initialSnap]
  const editor = designStore?.editor

  useEffect(() => {
    if (!open && editor) {
      setCameraToCurrentFrame(editor, { animate: true, isMobile: true })
    }
  }, [open, editor, designStore])

  useEffect(() => {
    setSnap(initialSnap)
  }, [initialSnap, drawersStore.snapResetObserver])

  if (initialRender.current) {
    if (editor) {
      const frame = getCurrentFrame(editor)
      initialRender.current = false
      const renderingBounds = editor.getRenderingBounds()
      const captionPadding = getCaptionPadding()
      const footer = document.querySelector(`#${MOBILE_DESIGN_FOOTER_ID}`)

      const viewportScreenBounds = editor.getViewportScreenBounds()
      const reducedHeight =
        viewportScreenBounds.height - initialSnap * window.innerHeight + (footer?.getBoundingClientRect()?.height || 0)
      const reductionFactor = reducedHeight / viewportScreenBounds.height
      const frameHeight = (frame && editor.getShapePageBounds(frame)?.height) || 0
      const currentHeight = frameHeight + captionPadding.top + captionPadding.bottom - renderingBounds.y
      const newHeight = currentHeight / reductionFactor
      const difference = newHeight - currentHeight

      const padding = { top: 0, bottom: difference }
      setCameraToCurrentFrame(editor, { animate: true, isMobile: true, padding })
    }
  }

  return (
    <Drawer.Root
      modal={false}
      onClose={onClose}
      snapPoints={snapPoints}
      activeSnapPoint={snap}
      setActiveSnapPoint={setSnap}
      open={open}
      {...rest}
    >
      <Drawer.Portal>
        <Drawer.Content fullHeight>
          <div data-vaul-no-drag={disableDragToClose}>
            <Drawer.InnerContent color="var(--light-100)" isSnappedTop={snap === 1}>
              <Drawer.Top>
                {onBack ? <Drawer.Back onPress={onBack} variant="ghost" /> : <div />}
                <Drawer.Title asChild>
                  <Heading lineClamp={1} level={5}>
                    {title}
                  </Heading>
                </Drawer.Title>
                {closeButton ? closeButton : <Drawer.Close label="Close" variant="ghost" position="right" />}
                <Drawer.Description />
              </Drawer.Top>
              <Drawer.ScrollableContainer snap={Number(snap || 1)} padding={padding}>
                {children}
              </Drawer.ScrollableContainer>
            </Drawer.InnerContent>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
})
